import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const CYCLE = ["Learning", "Improvement", "Confidence", "Increased pressure", "Loss of control", "Spiral"];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="border-b border-hairline" data-testid="home-hero">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 pt-20 md:pt-32 pb-20 md:pb-32">
          <h1 className="font-display text-[2.05rem] sm:text-5xl md:text-[5.5rem] lg:text-[6.25rem] leading-[1.02] tracking-tight max-w-[68rem] rise rise-1" data-testid="hero-headline">
            You don’t lose because<br/>you don’t know<br className="md:hidden"/> what to do.
          </h1>
          <h2 className="font-display-medium text-2xl sm:text-3xl md:text-5xl lg:text-[3.5rem] mt-6 md:mt-8 max-w-[60rem] rise rise-2" data-testid="hero-subheadline">
            You lose because, under pressure, you become someone who can’t<span className="whitespace-nowrap">&nbsp;do it.</span>
          </h2>
          <div className="mt-12 md:mt-16 flex flex-wrap items-center gap-6 rise rise-3">
            <Link to="/ebook" data-testid="hero-cta-primary" className="group inline-flex items-center gap-3 bg-ink text-white px-7 py-3.5 label-mono hover:bg-leaf transition-colors">
              Get the free book <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <p className="mt-4 label-mono text-muted text-[10px]">125 pages · free · delivered by email</p>
        </div>
      </section>

      {/* REAL CHALLENGE */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <h3 className="md:col-span-4 section-label">The real challenge for traders</h3>
          <p className="md:col-span-7 md:col-start-6 text-lg md:text-xl text-muted leading-[1.5] md:leading-[1.8]">
            Most traders know their rules, their method and the importance of discipline. But under stress and mounting pressure, even skilled traders lose control. Despite knowing what to do, they break their own rules at the toughest moments.
          </p>
        </div>
      </section>

      {/* NOT JUST PSYCHOLOGY */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <h3 className="md:col-span-4 section-label">It’s not just psychology</h3>
          <div className="md:col-span-7 md:col-start-6 text-lg md:text-xl text-muted leading-[1.5] md:leading-[1.8] space-y-6">
            <p>Trading breakdowns do not happen only in the mind. Under stress, your state changes before your judgement does. Pressure and accumulated load shift how the same decision gets made, and following your plan becomes far harder than it was when you wrote it.</p>
            <p>The spiral is not about weakness, mindset or willpower. It is what happens when state overrides even the strongest principles.</p>
          </div>
        </div>
      </section>

      {/* TRADING CYCLE */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <h3 className="section-label">The trading cycle</h3>
          <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-3 font-display text-2xl md:text-4xl lg:text-5xl leading-tight" data-testid="trading-cycle">
            {CYCLE.map((c, i) => (
              <span key={c} className="flex items-center gap-4">
                <span className={i === CYCLE.length - 1 ? "text-red-600" : "text-ink"}>{c}</span>
                {i < CYCLE.length - 1 && <span className="text-muted">→</span>}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* TRADING WITHIN LIMITS */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <h3 className="md:col-span-4 section-label">Trading within biological limits</h3>
          <div className="md:col-span-7 md:col-start-6 text-lg md:text-xl text-muted leading-[1.5] md:leading-[1.8] space-y-6">
            <p>The path forward is trading with respect for state. Control position size, and step back when pressure rises rather than after it has already cost something. Filter your trading days instead of assuming every day is available.</p>
            <p>Notice the early signs: tension, tighter focus and the urge to act. Pause before the spiral, not during it. Use rules that reduce pressure rather than rules that require more of you under it.</p>
            <p className="text-ink font-medium">You cannot out-discipline biology. Your edge exists within your limits.</p>
          </div>
        </div>
      </section>

      {/* TESTED IN PUBLIC */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <h3 className="md:col-span-4 section-label">Tested in public</h3>
          <div className="md:col-span-7 md:col-start-6 text-lg md:text-xl text-muted leading-[1.5] md:leading-[1.8]">
            <p>Beginning 15 September 2026, regular public live sessions will show the same rules, size restrictions and state decisions under real pressure. The permitted size will appear beside the size actually taken, with heart-rate context and the rule active at the moment, including red days, violations and legitimate no-trade sessions.</p>
            <p className="mt-8 label-mono text-muted text-[10px]">Live sessions begin 15 September 2026 · 9:00 New York time</p>
          </div>
        </div>
      </section>

      {/* THE COMPLETE BOOK — promotion for the finished book */}
      <section id="free-preview" data-testid="free-preview-section">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
            {/* COVER */}
            <div className="md:col-span-5" data-testid="ebook-cover">
              <img
                src="/the-cage-cover.webp"
                alt="THE CAGE book cover"
                className="block w-full max-w-sm h-auto"
                data-testid="home-book-cover"
              />
            </div>

            {/* COPY + CTA */}
            <div className="md:col-span-7">
              <div className="label-mono text-muted">The complete book</div>
              <h2 className="font-display mt-6 text-4xl md:text-6xl lg:text-[4rem] leading-[1.04] tracking-tight">
                Get THE CAGE free.
              </h2>
              <p className="mt-8 text-lg md:text-xl text-muted leading-[1.5] md:leading-[1.7] max-w-xl">
                A complete book drawn from nine years of trading and 168 journal entries: why experienced traders destroy what they have built under pressure, and the structure built to contain it.
              </p>
              <div className="mt-10">
                <Link
                  to="/ebook"
                  className="group inline-flex items-center gap-3 bg-ink text-white px-8 py-4 label-mono hover:bg-leaf transition-colors"
                  data-testid="home-get-free-book"
                >
                  Get the free book <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
              <p className="mt-3 label-mono text-muted text-[10px]">125 pages · free · delivered by email</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
